
import Vue from 'vue';
import { mapState, mapGetters, mapActions } from 'vuex';

import { RootActions, EventTypes } from '@/store/types';
import ResourceFile from '@/store/modules/resources/file';
import ResourceLink from '@/store/modules/resources/link';

import ResourceAssetCompleteTrainingForm from '@/components/forms/resource-asset-complete-training.vue';
import VideoModal from '@/components/layout/modal-video.vue';

export default Vue.extend({
  name: 'ResourceLinkBlock',
  components: {
    ResourceAssetCompleteTrainingForm,
  },
  props: ['link', 'parentThumbnail'],
  computed: {
    ...mapGetters('user/profile', ['isSuperAdmin', 'isAdmin']),
    ...mapState('resources', {
      resource: 'currentResource',
    }),
    thumbnail() {
      return this.parentThumbnail;
    },
    isTraining(): boolean {
      if (this.isSuperAdmin || this.isAdmin) {
        return false;
      }

      return typeof this.resource !== 'undefined' && this.resource.training === 1;
    },
    areAllFilesResolved() {
      if (!this.isTraining) {
        return true;
      }

      const unresolvedFile = (this.resource.files as ResourceFile[]).find((item) => !item.trainingComplete);

      return typeof unresolvedFile === 'undefined';
    },
    unresolvedLinks(): ResourceLink[] {
      if (!this.isTraining) {
        return [];
      }

      return this.resource.links.filter((item) => !item.trainingComplete);
    },
    showCompleteTrainingCheckbox(): boolean {
      if (!this.isTraining) {
        return false;
      }

      if (!this.areAllFilesResolved) {
        return false;
      }

      // If link training is completed.
      if (this.link.trainingComplete) {
        return true;
      }

      if (!this.unresolvedLinks.length) {
        return true;
      }

      return this.unresolvedLinks[0].id === this.link.id;
    },
    canOpen(): boolean {
      if (!this.isTraining) {
        return true;
      }

      if (
        this.resource.files.length === 1 &&
        this.resource.files[0].assetId === 0 &&
        this.resource.files[0].fid === 0 &&
        this.resource.files[0].id === 0
      ) {
        return true;
      }

      if (this.showCompleteTrainingCheckbox) {
        return true;
      }

      return false;
    },
    wistiaVideoId(): string | null {
      if (!this.link || !this.link.url) {
        return null;
      }

      const match = this.link.url.match(
        /^https?:\/\/(?:[\w-]+\.)?wistia\.(?:com|net)\/(?:medias|embed\/medias)\/([a-zA-Z0-9]+)(?:[/?#].*)?$/i,
      );

      if (!match || !match[1]) {
        return null;
      }

      return match[1];
    },
    isWistiaLink(): boolean {
      return this.wistiaVideoId !== null;
    },
  },
  methods: {
    ...mapActions([RootActions.DISPATCH_EVENT]),
    open(event) {
      if (!this.canOpen) {
        event.preventDefault();
        return;
      }

      if (this.isWistiaLink && this.wistiaVideoId) {
        event.preventDefault();
        this.$modal.show(VideoModal, { id: this.wistiaVideoId, popover: false }, { name: 'resource-link-video' });
      }

      this[RootActions.DISPATCH_EVENT]({
        type: EventTypes.DOWNLOAD_ALL,
        eventObj: { resource: this.resource, link: this.link },
      });
    },
  },
});
